import { KEY_ROOM_USAGE } from "@/data/models/ProjectDetails";

/** The type marker of a single-choice field; the room usage is one in every version that has it. */
const SINGLE_CHOICE = "E";

/** The type markers of a Beschreibung row: single choice, multiple choice, free text, sketch value. */
const TYPE_MARKERS = new Set([SINGLE_CHOICE, "M", "T", "A"]);

/** Starts a new row of chips in the app; not an option, and it takes no index. */
const ROW_SPLIT = "/";

/** `(XT30) Sonstige`: an option that also takes a free value. The label is the text after it. */
const INPUT_MASK = /^\(X[^)]*\)\s*/;

/**
 * The room usage options (`Raum0020`) of one Beschreibung file, in the order whose position a room
 * stores: `options[11]` is what a stored `"11"` means in this version.
 *
 * The rules are IRES Project's own parser's, checked against every version on 2026-09-28:
 *
 * - the separator is the last character of the first line (`;`, or `,` in 3.10, 3.16 and 3.17),
 * - the options are the cells after the type marker; blank cells and `/` row splits are not options,
 * - an input mask such as `(XT30)` and a leading `=` (the default choice) are not part of the label.
 *
 * The order differs between versions (WC is option 2 from 5.26 on, option 8 before), so a stored
 * position must only ever be read against its project's pinned version.
 *
 * @param csvText the whole `info.{version}.csv`
 * @returns the option labels, or `null` when the version has no single-choice room usage (1.03 to
 * 1.42 and 2.80 have no `Raum0020` row, and in 2.12 it is a sketch value)
 */
export function parseRoomUsageOptions(csvText: string): string[] | null {
  const lines = csvText.split(/\r\n|\r|\n/);
  const separator = lines[0]?.trimEnd().slice(-1) || ";";
  const row = lines.find((line) => line.startsWith(KEY_ROOM_USAGE + separator));

  if (!row) {
    return null;
  }

  const cells = row.split(separator).map((cell) => cell.trim());
  const typeIndex = cells.findIndex((cell, index) => index > 0 && TYPE_MARKERS.has(cell));

  if (typeIndex === -1 || cells[typeIndex] !== SINGLE_CHOICE) {
    return null;
  }

  return cells
    .slice(typeIndex + 1)
    .filter((cell) => cell !== "" && cell !== ROW_SPLIT)
    .map((cell) => cell.replace(/^=/, "").replace(INPUT_MASK, "").trim());
}
